import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Eye, SquarePen } from "lucide-react";
import { useToast } from "@shared/hooks/use-toast";
import { GenericTable } from "@shared/components/GenericTable";
import { TableColumn } from "@shared/types/table";
import { PAGE_SIZE } from "@shared/utils/constants";
import { TemplateFiltersPanel } from "../filter/filter";
import { useTemplateList } from "../hooks";
import { useNotificationChoices } from "@features/ux/hooks";

interface TemplateData {
  id: string;
  templateId: string;
  category: string;
  channel: string;
  templateName: string;
  version: string;
  createdBy: string;
  updatedBy: string;
    lastModifiedDate: string;
    statusDisplay:any;
}

interface TemplateFilters {
  channel: string;
  audienceType: string;
  status: string;
}

const TemplateList: React.FC = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();

  // ✅ Read all params from URL
  const searchQuery = decodeURIComponent(searchParams.get("search") || "");
  const channel = searchParams.get("channel") || "";
  const audienceType = searchParams.get("audienceType") || "";
  const status = searchParams.get("status") || "";
  const currentPage = parseInt(searchParams.get("page") || "1");

  const { data: audienceTypeData } = useNotificationChoices("audience_type");
  const { data: channelData } = useNotificationChoices("channel_choice");
  const { data: statusData } = useNotificationChoices("status");

  // ✅ Pass currentPage to the hook
  const { data: templateListData } = useTemplateList(
    currentPage,   
    searchQuery,
    channel,
    audienceType,
    status
  );

  const templatesList = templateListData?.results || [];

  // ✅ Calculate pagination state from API response
  const pagination = useMemo(() => {
    if (!templateListData) {
      return {
        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        itemsPerPage: PAGE_SIZE,
        startItem: 0,
        endItem: 0,
      };
    }

    const totalItems = templateListData.count || 0;
    const totalPages = Math.ceil(totalItems / PAGE_SIZE);
    const startItem = (currentPage - 1) * PAGE_SIZE + 1;
    const endItem = Math.min(currentPage * PAGE_SIZE, totalItems);

    return {
      currentPage,
      totalPages,
      totalItems,
      itemsPerPage: PAGE_SIZE,
      startItem,
      endItem,
    };
  }, [templateListData, currentPage]);

  // pendingFilters: UI state for user edits
  const [pendingFilters, setPendingFilters] = useState<TemplateFilters>({
    channel: decodeURIComponent(channel),
    audienceType: decodeURIComponent(audienceType),
    status: decodeURIComponent(status),
  });

  const audienceTypeOptions =
    audienceTypeData?.map((audienceType) => ({
      value: audienceType.key,
      label: audienceType.value,
    })) || [];
  
  const channelsOptions =
    channelData?.map((channel) => ({
      value: channel.key,
      label: channel.value,
    })) || [];
  
  const statusOptions =
    statusData?.map((status) => ({
      value: status.key,
      label: status.value,
    })) || [];

  // sync pendingFilters when URL params change
  useEffect(() => {
    setPendingFilters({
      channel: decodeURIComponent(channel),
      audienceType: decodeURIComponent(audienceType),
      status: decodeURIComponent(status),
    });
  }, [channel, audienceType, status]);

  const getStatusBadge = (status: string) => {
    const base =
      "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium";

    switch (status?.toLowerCase()) {
      case "active":
        return (
          <span className={`${base} bg-green-100 text-green-800`}>
            Active
          </span>
        );

      case "inactive":
        return (
          <span className={`${base} bg-red-100 text-red-800`}>
            Inactive
          </span>
        );

      case "draft":
        return (
          <span className={`${base} bg-yellow-100 text-yellow-800`}>
            Draft
          </span>
        );

      default:
        return (
          <span className={`${base} bg-gray-200 text-gray-800`}>
            {status}
          </span>
        );
    }
  };

  const resetFilters = () => {
    const newParams: any = {};
    // ✅ Keep page when resetting filters (optional - you might want to reset to page 1)
    // newParams.page = "1";  // Uncomment to reset to page 1 when clearing filters
    
    setSearchParams(newParams);
  };

  const handleSearch = (searchTerm: string) => {
    const newParams: Record<string, string> = {
      ...(channel && { channel }),
      ...(audienceType && { audienceType }),
      ...(status && { status }),
      page: "1",
    };

    if (searchTerm.trim()) {
      newParams.search = searchTerm.trim();
    }

    setSearchParams(newParams);
  };

  const handlePageChange = (page: number) => {
    const newParams: Record<string, string> = {
      ...Object.fromEntries(searchParams.entries()),
      page: page.toString(),
    };
    setSearchParams(newParams);
  };

  const columns: TableColumn<TemplateData>[] = [
{
      key: "templateName",
      header: "Template Name",
    },
    {
      key: "channelName",
      header: "Channel",
      render: (value: string) => (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
          {value}
        </span>
      ),
    },
    {
      key: "version",
      header: "Version",
    },
    {
      key: "templateCategory",
      header: "Category",
      render: (value: string) => (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
          {value}
        </span>
      ),
    },
    {
      key: "audienceTypeName",
      header: "Audience type",
    },
    {
      key: "statusDisplay",
      header: "Status",
      render: (value: string) => getStatusBadge(value),
    },
    {
      key: "createdUserRemoteName",
      header: "Created By",
    },
   {
  key: "lastModifiedUserRemoteName",
  header: "Updated By",
  render: (value, row) => (
    <div>
      <p className="font-medium">{value ?? "NA"}</p>
      <p className="text-xs text-muted-foreground">
        {row.lastModifiedDate ?? "NA"}
      </p>
      
    </div>
  ),
},
  ];

  const actions = [
    {
      label: "View Detail",
      icon: <Eye className="h-4 w-4 mr-2" />,
      disabled: (row: TemplateData) =>
        row.statusDisplay?.toLowerCase() === "draft",
      onClick: (row: TemplateData) => {
        navigate(`/communication-hub/templates/view/${row.id}/${row.templateName}`);
      },
    },
    {
      label: "Edit",
      icon: <SquarePen className="h-4 w-4 mr-2" />,
      onClick: (row: TemplateData) => {
        navigate(`/communication-hub/templates/edit/${row.id}`);
      },
    },
  ];

  const filterPanel = (
    <TemplateFiltersPanel
      channel={channel}
      audienceType={audienceType}
      status={status}
      channelList={channelsOptions}
      audienceTypeList={audienceTypeOptions}
      statusList={statusOptions}
      onApplyFilters={(filters) => {
        const newParams: any = {
          page: "1", 
          ...(filters.channel && { channel: filters.channel }),
          ...(filters.audienceType && { audienceType: filters.audienceType }),
          ...(filters.status && { status: filters.status }),
          ...(searchQuery && { search: searchQuery }), 
        };
        setSearchParams(newParams);
      }}
      onResetFilters={resetFilters}
    />
  );

  const hasActiveFilters = channel || audienceType || status;

  return (
    <div className="space-y-6 p-1">
      <GenericTable
        data={templatesList}
        columns={columns}
        rowKey="id"
        selectable={false}
        actions={actions}
        actionsType="icons"
        emptyMessage="No templates found matching your criteria"
        pagination={pagination}  
        onPageChange={handlePageChange}
        onSearch={handleSearch}
        searchPlaceholder="Search by template name..."
        filterPanel={filterPanel}
        filters={
          hasActiveFilters ? (
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">
                {[!!channel, !!audienceType, !!status].filter(Boolean).length}{" "}
                filter(s) active
              </span>
            </div>
          ) : null
        }
      />
    </div>
  );
};

export default TemplateList;